import React, { useContext, useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { PhrasesContext } from "../../../context/phrasesContext";
import { UploadCourtDocument, UploadReceipt } from "../../../services/Common";
import FalidUploadReciept from "../../FalidUploadReciept";
import SuccessUploadReciept from "../../SuccessUploadReciept";
import SharedModal from "../../happiness/SharedModal";
import { Loader } from "../../utils";

import DatePicker, { CalendarContainer } from "react-datepicker";
import { ar, enUS } from "date-fns/locale"; // use ar (not arSA) to avoid missing-locale errors
import "react-datepicker/dist/react-datepicker.css";
import "./receipt-datepicker.css";

/* ---------- helpers ---------- */
const yearsRange = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

/* ---------- custom datepicker ---------- */

const ReceiptDatePicker = ({
  lang,
  selectedDate,
  setSelectedDate,
  onCancel, // optional: extra side-effect on close
  errors,
  setValue, // Added setValue to props
}) => {
  const isRTL = lang !== "en";
  const locale = lang === "en" ? enUS : ar;

  // control popper + temporary selection
  const [isOpen, setIsOpen] = useState(false);
  const [tempDate, setTempDate] = useState(null);

  const now = new Date();
  const years = useMemo(
    () => yearsRange(now.getFullYear() - 100, now.getFullYear() + 10),
    [now]
  );
  const months = useMemo(
    () =>
      lang === "en"
        ? [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ]
        : [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
    [lang]
  );

  const CustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    changeMonth,
    changeYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => {
    const m = date.getMonth();
    const y = date.getFullYear();
    return (
      <div
        className={`flex items-center justify-between py-2 w-full ${
          isRTL ? "flex-row-reverse" : ""
        }`}
      >
        {/* Month controls */}
        <div className="flex items-center gap-2 w-1/2">
          <button
            type="button"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            className={`p-2 rounded hover:bg-gray-100 disabled:opacity-40 ${
              lang === "en" ? "" : "rotate-180"
            }`}
            aria-label="Prev Month"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path
                d="M15 18l-6-6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>
          <div className="relative inline-block">
            <select
              className="!appearance-none border-none text-sm py-[10px] px-[25px] pr-8 [&::-ms-expand]:hidden focus:!border-none bg-none" // hides arrow in IE/Edge
              value={m}
              onChange={(e) => changeMonth(Number(e.target.value))}
            >
              {months.map((label, idx) => (
                <option key={idx} value={idx}>
                  {label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
              ▼
            </span>
          </div>
          <button
            type="button"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            className={`p-2 rounded hover:bg-gray-100 disabled:opacity-40 ${
              lang === "en" ? "" : "rotate-180"
            }`}
            aria-label="Next Month"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path
                d="M9 18l6-6-6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        {/* Year controls */}
        <div className="flex items-center gap-2 w-1/2">
          <button
            type="button"
            onClick={() => changeYear(y - 1)}
            className={`p-2 rounded hover:bg-gray-100 ${
              lang === "en" ? "" : "rotate-180"
            }`}
            aria-label="Prev Year"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path
                d="M15 18l-6-6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>

          <div className="relative inline-block">
            <select
              className="!appearance-none border-none text-sm py-[10px] px-[25px] pr-8 [&::-ms-expand]:hidden focus:border-none bg-none" // hides arrow in IE/Edge
              value={y}
              onChange={(e) => changeYear(Number(e.target.value))}
            >
              {years.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>

            {/* Custom arrow */}
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
              ▼
            </span>
          </div>

          <button
            type="button"
            onClick={() => changeYear(y + 1)}
            className={`p-2 rounded hover:bg-gray-100 ${
              lang === "en" ? "" : "rotate-180"
            }`}
            aria-label="Next Year"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path
                d="M9 18l6-6-6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const CustomContainer = ({ className, children }) => (
    <CalendarContainer className={className}>
      <div>{children}</div>
      <div className="ea-datepicker__footer">
        {/* Cancel = close without applying temp */}
        <span
          className="ea-action ea-action--cancel"
          onClick={() => {
            setTempDate(selectedDate || null); // revert temp
            setIsOpen(false);
            onCancel && onCancel();
          }}
          role="button"
          tabIndex={0}
        >
          {lang === "en" ? "Cancel" : "إلغاء"}
        </span>

        {/* OK = apply temp to parent, close */}
        <span
          className="ea-action ea-action--ok"
          onClick={() => {
            setSelectedDate(tempDate || null);
            if (typeof setValue === "function") {
              setValue("SelectedDate", tempDate || null, {
                shouldValidate: true,
              });
            }
            setIsOpen(false);
            onCancel && onCancel();
          }}
          role="button"
          tabIndex={0}
        >
          {lang === "en" ? "OK" : "تم"}
        </span>
      </div>
    </CalendarContainer>
  );

  return (
    <div className="relative receipt-date-picker-scope">
      <DatePicker
        /* control the popper */
        open={isOpen}
        autoFocus={false}
        preventOpenOnFocus={true} // available in react-datepicker
        onFocus={(e) => e.target.blur()} // forces no focus
        onInputClick={() => {
          setTempDate(selectedDate || null);
          setIsOpen(true);
        }}
        onCalendarOpen={() => setTempDate(selectedDate || null)}
        onCalendarClose={() => {
          // apply selected date even if OK was not clicked
          const finalDate = tempDate ?? selectedDate;
          setSelectedDate(finalDate);
          if (typeof setValue === "function") {
            setValue("SelectedDate", finalDate, { shouldValidate: true });
          }
          setIsOpen(false);
          onCancel && onCancel();
        }}
        /* while open, drive selection from tempDate */
        selected={tempDate != null ? tempDate : selectedDate}
        onChange={(d) => {
          setTempDate(d);
          setSelectedDate(d);
          if (typeof setValue === "function") {
            setValue("SelectedDate", d, { shouldValidate: true });
          }
        }}
        onChangeRaw={(e) => {
          const allowed = /^[0-9/-]*$/; // only digits, slash, dash
          if (!allowed.test(e.target.value)) {
            e.preventDefault();
          }
        }}
        shouldCloseOnSelect={false}
        locale={locale}
        calendarClassName="ea-datepicker"
        popperClassName="ea-datepicker-popper receipt-popper-scope"
        showPopperArrow={false}
        renderCustomHeader={(p) => <CustomHeader {...p} />}
        calendarContainer={CustomContainer}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        className={`border w-full h-[40px] px-3 rounded-md !text-[12px]  text-[#010202] shadow-sm leading-[2] font-["system-ui"] focus:border-gold-100  focus:ring-gold-100 ${
          errors?.SelectedDate ? "!border-red-600" : "border-gray-300"
        }`}
        placeholderText={lang === "en" ? "Select date" : "اختر التاريخ"}
        popperPlacement={isRTL ? "bottom-start" : "bottom-end"}
        maxDate={new Date()}
        weekStartsOn={0}

        /* remove unsupported prop if it causes warnings */
        // showIcon
        // calendarIconClassname={`ms-full mt-[4px] ${lang === "en" ? "right-0" : "left-0"}`}
      />
      <img
        src="/assets/img/CalendarDots.svg"
        alt=""
        className={`absolute top-[10px] ${
          lang === "en" ? "right-3" : "left-3"
        }`}
      />
    </div>
  );
};

/* ---------- main form ---------- */
const ReceiptForm = ({
  receiptStatus,
  setReceiptStatus,
  uploadReceiptHandler,
  service,
  successUploadReciept,
  setSuccessUploadReciept,
  setFalidUploadReciept,
  falidUploadReciept,
}) => {
  const langStorage = window.localStorage.getItem("lang");
  const lang = langStorage === "en" ? "en" : "ar";
  const [enPhrase, arPhrase] = useContext(PhrasesContext);
  const inputFile = useRef(null);

  const [file, setFile] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null); // File | null
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [selectedDate, setSelectedDate] = useState(null); // Date | null
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [fileUrl, setFileUrl] = useState();
  const [uploadedHash, setUploadedHash] = useState();
  const [showImage, setShowImage] = useState(false);
  const [validationError, setValidationError] = useState("");

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm();

  const getBase64 = (file, cb) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      cb(reader.result);
      setFileUrl(reader.result);
    };
  };

  const updateData = (data) => {
    setLoading(true);
    UploadReceipt(data).then((res) => {
      if (res.success === true) {
        setSuccessUploadReciept(true);
        setLoading(false);
      } else {
        setFalidUploadReciept(true);
        setLoading(false);
      }
    });
  };

  const onSubmit = async () => {
    setLoading(true);
    const SelectedDate = selectedDate
      ? selectedDate.toLocaleDateString("en-US")
      : "";

    const recepitInfo = {
      ServiceId: service.id,
      CaseNumber: service.caseNumber,
      Receipt: uploadedFile,
      AccountNumber: accountNumber,
      AccountName: accountName,
      SelectedDate: SelectedDate,
    };

    console.log("recepitInfo", recepitInfo);

    updateData(recepitInfo);
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
    ];
    if (!allowedTypes.includes(f.type)) {
      setValidationError(
        lang === "en"
          ? enPhrase["lblOnlyImgAndPDF"]
          : arPhrase["lblOnlyImgAndPDF"]
      );
      return;
    }

    if (f.size > 10 * 1024 * 1024) {
      setValidationError(
        lang === "en"
          ? "File size must be under 10MB."
          : "يجب أن يكون حجم الملف أقل من 10 ميجابايت"
      );
      setValue("fileupload", "");
      return;
    }

    setValidationError("");
    setUploadedFile(f);

    // Call getBase64 to generate local preview
    getBase64(f, (res) => setFile(res));

    // Call the API and set the returned URL
    setLoadingUpload(true);
    try {
      const response = await UploadCourtDocument(f); // Pass the File object
      if (response && response.fileUrl) {
        setFileUrl(response.fileUrl); // Store the public URL from the API response
        setUploadedHash(response?.hash);
      } else {
        setValidationError(
          lang === "en" ? "Failed to upload file." : "فشل تحميل الملف."
        );
      }
    } catch (err) {
      setValidationError(
        lang === "en" ? "Failed to upload file." : "فشل تحميل الملف."
      );
      console.error(err);
    } finally {
      setLoadingUpload(false);
    }
  };

  console.log("setLoadingUpload", loadingUpload);

  if (successUploadReciept) {
    return (
      <SharedModal
        isSuccessUpload
        openModal={successUploadReciept}
        setOpenModal={setSuccessUploadReciept}
      >
        <SuccessUploadReciept
          successUploadReciept={successUploadReciept}
          setSuccessUploadReciept={setSuccessUploadReciept}
          setReceiptStatus={setReceiptStatus}
          service={service}
        />
      </SharedModal>
    );
  }

  if (falidUploadReciept) {
    return (
      <SharedModal
        isFaildUpload
        openModal={falidUploadReciept}
        setOpenModal={setFalidUploadReciept}
      >
        <FalidUploadReciept
          lang={lang}
          setFalidUploadReciept={setFalidUploadReciept}
          setUploadedFile={setUploadedFile}
          setFile={setFile}
        />
      </SharedModal>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-[#eee2d0] w-full rounded-t-[16px]">
        <div className="h-[56px] w-full flex items-center px-[24px] justify-between">
          <h2 className="text-[14px] font-Almarai font-bold h-[16px] text-[#010202]">
            {lang === "en" ? enPhrase["btnuploadpr"] : arPhrase["btnuploadpr"]}
          </h2>
          <img
            src="/assets/img/close.svg"
            alt=""
            className="cursor-pointer"
            onClick={() => setReceiptStatus(false)}
          />
        </div>
      </div>

      <div className="mt-1 cursor-pointer flex p-[24px]">
        {loadingUpload ? (
          <div className="w-full flex items-center justify-center h-[143px] flex-col border-2  border-dashed bg-gold-10  rounded-md border-[#B4B4B4]">
            <Loader
              uploadReceipt={true}
              color={"mainColor"}
              imageSrc="/assets/img/add-request/LogoIcon.svg"
              isRequest
              isUpload
              circleThickness={1} // Outer circle thickness
              pathThickness={1} // Path thickness (spinner line)
            />
            <p className="text-[12px] font-Almarai text-[#B68A35] text-center font-normal leading-[14px]">
              {lang === "en"
                ? "Payment receipt is being attached"
                : "جاري إرفاق إيصال الدفع"}
            </p>
          </div>
        ) : file ? (
          <div className="w-full md:w-[412px] h-[104px] p-[16px] rounded-[8px] border-[1px] border-solid border-[#EEE2D0] bg-[#fff] flex items-center justify-start md:justify-center gap-[16px]">
            <img
              src="/assets/img/submission.svg"
              alt="upload"
              className="w-[25px] h-[30px]"
            />
            {showImage ? (
              <SharedModal
                showImage
                setOpenModal={setShowImage}
                openModal={showImage}
              >
                <div className="w-full rounded-t-[16px]">
                  <div className="h-[56px] w-full flex items-center justify-end px-[24px] bg-[#eee2d0]">
                    <img
                      src="/assets/img/close.svg"
                      alt=""
                      className="cursor-pointer"
                      onClick={() => setShowImage(false)}
                    />
                  </div>
                  {uploadedFile?.type?.includes("pdf") ? (
                    <embed
                      src={fileUrl}
                      type="application/pdf"
                      className="w-full h-[288px] rounded-b-[16px]"
                    />
                  ) : (
                    <img className="w-full h-[288px]" alt="" src={fileUrl} />
                  )}
                </div>
              </SharedModal>
            ) : (
              <div className="flex flex-col h-[38px] items-start justify-start">
                <p className="text-[14px] font-Almarai font-bold text-[#361e12] w-[120px] sm:w-[237px] overflow-hidden ">
                  {uploadedFile?.name}
                </p>
                <p className="text-[12px] font-Almarai text-[#6e6e6e]">
                  {uploadedFile ? Math.round(uploadedFile.size / 1024) : 0}KB{" "}
                </p>
              </div>
            )}

            <img
              src="/assets/img/view.svg"
              alt="view-icon"
              className="w-[24px] h-[24px] cursor-pointer"
              onClick={() => setShowImage(!showImage)}
            />
            <img
              src="/assets/img/trash-can.svg"
              alt="delete-icon"
              className="w-[18px] h-[18px] cursor-pointer"
              onClick={() => {
                setFile("");
                setUploadedFile(null);
              }}
            />
          </div>
        ) : (
          <div className="w-full flex flex-col gap-[24px]">
            <div>
              <label
                htmlFor="fileupload"
                className={`relative cursor-pointer flex flex-col w-full items-center justify-center h-[123px] border-2  border-dashed bg-gold-10  rounded-md px-6 pt-[46px] pb-[45px] font-medium text-gold-100 focus:!border-gold-100 focus:ring-gold-100 ${
                  errors.fileupload ? "border-red-600" : "border-[#B4B4B4]"
                }`}
              >
                {!file && errors.fileupload && (
                  <span
                    role="alert"
                    className="text-red-600 mx-3 text-xs mb-[10px]"
                  >
                    {lang === "en"
                      ? enPhrase["lblUploadRecipt"]
                      : arPhrase["lblUploadRecipt"]}
                  </span>
                )}
                <div className="flex flex-col gap-[6px] items-center">
                  <div className="flex items-center gap-[8px]">
                    {!file && (
                      <img
                        src="/assets/img/uploadFile.svg"
                        alt="Upload-File"
                        className="w-[32px] h-[32px]"
                      />
                    )}
                    {!file && (
                      <p className="text-[12px] text-[#6C4527] font-bold">
                        {lang === "en"
                          ? enPhrase["lblUploadReceipt"]
                          : arPhrase["lblUploadReceipt"]}
                      </p>
                    )}
                  </div>
                  <p className="text-[12px] text-[#B68A35] font-normal text-center w-full md:w-[163px]">
                    {lang === "en"
                      ? enPhrase["lblValidationInputDocument"]
                      : arPhrase["lblValidationInputDocument"]}
                  </p>
                </div>

                <input
                  id="fileupload"
                  name="fileupload"
                  ref={inputFile}
                  type="file"
                  accept="image/png, image/jpeg, application/pdf, image/jpg"
                  {...register("fileupload", { required: true })}
                  onChange={handleFileChange}
                  className="sr-only"
                />
              </label>
              {validationError && (
                <p className="text-red-700 text-xs mt-2 mb-[8px]">
                  {validationError}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="w-full flex flex-col gap-[24px] px-[24px] mb-[24px]">
        <div className="w-full flex flex-col gap-[6px]">
          <p className="text-[#010202] font-Almarai text-[12px] leading-normal font-bold">
            {lang === "en"
              ? "Bank Account Number"
              : "ادخل رقم الحساب البنكي المُحول منه"}
          </p>
          {/* <input
            type="text"
            name="AccountNumber"
            key="AccountNumber"
            maxLength={16}
            placeholder={
              lang === "en"
                ? "Bank Account Number"
                : "ادخل رقم الحساب البنكي المُحول منه"
            }
            onKeyDown={(e) => {
              if (
                [
                  "Backspace",
                  "Delete",
                  "Tab",
                  "Escape",
                  "Enter",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              )
                return;
              if (!/^\d$/.test(e.key)) e.preventDefault();
            }}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData("text");
              if (!/^\d+$/.test(pastedText)) {
                e.preventDefault(); // Block non-digit paste
              } else if ((accountNumber + pastedText).length > 16) {
                e.preventDefault(); // Block if pasted value exceeds 16 digits
              }
            }}
            aria-invalid={errors.AccountNumber ? "true" : "false"}
            {...register("AccountNumber", {
              required: true,
              onChange: (e) => setAccountNumber(e.target.value),
            })}
            className={`mt-1 block w-full rounded-md h-[40px] shadow-sm focus:border-gold-100 focus:ring-gold-100 !text-[12px] sm:text-sm leading-[20px] ${
              errors.AccountNumber ? " border-red-600" : " border-gray-300"
            }`}
          /> */}
          {/* {errors.AccountNumber && (
            <span role="alert" className="text-xs text-red-600">
              {lang === "en"
                ? "Please Enter the source bank account number"
                : " من فضلك ادخل رقم الحساب البنكي المُحول منه"}
            </span>
          )} */}

          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            enterKeyHint="done"
            name="AccountNumber"
            maxLength={16}
            placeholder={
              lang === "en"
                ? "Bank Account Number"
                : "ادخل رقم الحساب البنكي المُحول منه"
            }
            aria-invalid={errors.AccountNumber ? "true" : "false"}
            {...register("AccountNumber", {
              required: true,

              onChange: (e) => {
                // sanitize on Android (and everywhere)
                const digits = e.target.value.replace(/\D/g, "").slice(0, 16);
                setAccountNumber(digits);
                // push the sanitized value back to RHF and trigger validation
                e.target.value = digits;
                setValue("AccountNumber", digits, { shouldValidate: true });
              },
            })}
            onInput={(e) => {
              // also catch IME/composition & autocorrect cases
              const digits = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 16);
              if (digits !== e.currentTarget.value) {
                e.currentTarget.value = digits;
                setValue("AccountNumber", digits, { shouldValidate: true });
                setAccountNumber(digits);
              }
            }}
            onPaste={(e) => {
              const pasted = e.clipboardData.getData("text");
              const digits = pasted.replace(/\D/g, "");
              if (!digits) e.preventDefault();
            }}
            className={`mt-1 block w-full rounded-md h-[40px] shadow-sm focus:border-gold-100 focus:ring-gold-100 font-["system-ui"]  !text-[12px] sm:text-sm leading-[20px] ${
              errors.AccountNumber ? " border-red-600" : " border-gray-300"
            }`}
          />
          {errors.AccountNumber && (
            <span role="alert" className="text-xs text-red-600">
              {lang === "en"
                ? "Please Enter the source bank account number ( only numbers )"
                : " من فضلك ادخل رقم الحساب البنكي المُحول منه ( ارقام فقط )"}
            </span>
          )}
        </div>

        <div className="w-full flex flex-col gap-[6px]">
          <p className="text-[#010202] font-Almarai text-[12px] leading-normal font-bold">
            {lang === "en" ? "Account Holder's Name" : "ادخل اسم صاحب الحساب"}
          </p>
          {/* <input
            type="text"
            name="AccountName"
            key="AccountName"
            maxLength={40}
            placeholder={
              lang === "en" ? "Account Holder's Name" : "ادخل اسم صاحب الحساب"
            }
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData("text");

              // If it contains digits, block the paste and show error
              if (/\d/.test(pastedText)) {
                e.preventDefault();
              }
            }}
            onKeyDown={(e) => {
              if (
                [
                  "Backspace",
                  "Delete",
                  "Tab",
                  "Escape",
                  "Enter",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              )
                return;
              if (/^\d$/.test(e.key)) e.preventDefault();
            }}
            aria-invalid={errors.AccountName ? "true" : "false"}
            // {...register("AccountName", { required: true })}
            {...register("AccountName", {
              required: true,
              onChange: (e) => setAccountName(e.target.value),
            })}
            className={`mt-1 block w-full rounded-md h-[40px] shadow-sm focus:border-gold-100 focus:ring-gold-100 !text-[12px] sm:text-sm leading-[20px] ${
              errors.AccountName ? " border-red-600" : " border-gray-300"
            }`}
          />
          {errors.AccountName && (
            <span role="alert" className="text-xs text-red-600">
              {lang === "en"
                ? "Please enter the account holder's name."
                : " من فضلك ادخل اسم صاحب الحساب"}
            </span>
          )} */}

          <input
            type="text"
            inputMode="text"
            autoCapitalize="words"
            name="AccountName"
            maxLength={40}
            placeholder={
              lang === "en" ? "Account Holder's Name" : "ادخل اسم صاحب الحساب"
            }
            aria-invalid={errors.AccountName ? "true" : "false"}
            {...register("AccountName", {
              required: true,
              validate: (v) =>
                /^[\p{L}\s'.-]{1,40}$/u.test(v) ||
                (lang === "en"
                  ? "Letters/spaces only (1–40)."
                  : "أحرف ومسافات فقط (1–40)."),
              onChange: (e) => {
                // remove digits and disallowed symbols
                const cleaned = e.target.value
                  .replace(/\d+/g, "") // no numbers
                  .replace(/[^ \p{L}'.-]/gu, "") // keep letters (all langs), space, ' . -
                  .slice(0, 40);
                if (cleaned !== e.target.value) e.target.value = cleaned;
                setAccountName(cleaned);
                setValue("AccountName", cleaned, { shouldValidate: true });
              },
            })}
            onInput={(e) => {
              const cleaned = e.currentTarget.value
                .replace(/\d+/g, "")
                .replace(/[^ \p{L}'.-]/gu, "")
                .slice(0, 40);
              if (cleaned !== e.currentTarget.value) {
                e.currentTarget.value = cleaned;
                setValue("AccountName", cleaned, { shouldValidate: true });
                setAccountName(cleaned);
              }
            }}
            onPaste={(e) => {
              const pasted = e.clipboardData.getData("text");
              if (/\d/.test(pasted)) e.preventDefault();
            }}
            className={`mt-1 block w-full rounded-md h-[40px] shadow-sm focus:border-gold-100 focus:ring-gold-100 font-["system-ui"] !text-[12px] sm:text-sm leading-[20px] ${
              errors.AccountName ? "border-red-600" : "border-gray-300"
            }`}
          />
          {errors.AccountName && (
            <span role="alert" className="text-xs text-red-600">
              {lang === "en"
                ? "Please enter the account holder's name ( letters/spaces only )."
                : "من فضلك ادخل اسم صاحب الحساب (حروف ومسافات فقط)."}
            </span>
          )}
        </div>

        <div className="w-full flex flex-col gap-[6px]">
          <p className="text-[#010202] font-Almarai text-[12px] leading-normal font-bold">
            {lang === "en" ? "Date of Transfer" : " تاريخ التحويل"}
          </p>

          <div className="relaive w-full">
            <ReceiptDatePicker
              errors={errors}
              lang={lang}
              {...register("SelectedDate", { required: true })}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              setValue={setValue} // ✅ Pass the actual setValue
              onCancel={() => {
                if (document.activeElement instanceof HTMLElement)
                  document.activeElement.blur();
              }}
            />
          </div>

          {errors.SelectedDate && (
            <span role="alert" className="text-xs text-red-600">
              {lang === "en"
                ? "Please select a date"
                : " من فضلك ادخل تاريخ التحويل"}
            </span>
          )}
        </div>
      </div>

      <div className="px-[24px] mb-[24px]">
        <button
          onClick={handleSubmit(onSubmit)}
          type="submit"
          disabled={!!validationError || loadingUpload}
          className={`w-full h-[44px] ${
            loadingUpload
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#b68a35] cursor-pointer"
          } flex items-center justify-center gap-[8px] rounded-[4px]`}
        >
          <p className="text-[14px] font-Almarai font-[700] text-[#fff] leading-normal">
            {lang === "en" ? enPhrase["btnSend"] : arPhrase["btnSend"]}
          </p>
          {loading && <Loader color={"white"} />}
        </button>
      </div>

      {/* {successUploadReciept && (
        <SharedModal
          isSuccessUpload
          openModal={successUploadReciept}
          setOpenModal={setSuccessUploadReciept}
        >
          <SuccessUploadReciept
            successUploadReciept={successUploadReciept}
            setSuccessUploadReciept={setSuccessUploadReciept}
            setReceiptStatus={setReceiptStatus}
            service={service}
          />
        </SharedModal>
      )}

      {falidUploadReciept && (
        <SharedModal
          isFaildUpload
          openModal={falidUploadReciept}
          setOpenModal={setFalidUploadReciept}
        >
          <FalidUploadReciept
            lang={lang}
            setFalidUploadReciept={setFalidUploadReciept}
            setUploadedFile={setUploadedFile}
          />
        </SharedModal>
      )} */}
    </div>
  );
};

export default ReceiptForm;
